import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Menu, X, User, LogOut, Settings, Calendar, Shield } from 'lucide-react';
import NotificationBell from './NotificationBell';

const ClientHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  
  // Debug: Log user object to see what fields are available
  useEffect(() => {
    if (user) {
      // console.log('User object in ClientHeader:', user);
    }
  }, [user]);
  
  // Check if we're on pages that use transparent header (dark hero below)
  const isHomePage = location.pathname === '/';
  const isAboutPage = location.pathname === '/about';
  const isHelpCenterPage = location.pathname === '/help';
  const isContactPage = location.pathname === '/contact';
  const isSafetyPage = location.pathname === '/safety';
  const isVendorSuccessPage = location.pathname === '/vendor/success';
  const isTermsPage = location.pathname === '/terms';
  const isPrivacyPage = location.pathname === '/privacy';

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  // Handle scroll detection for transparent/black header
  // Use requestAnimationFrame to optimize scroll-linked effects
  useEffect(() => {
    let rafId = null;
    let ticking = false;
    
    // Check initial scroll position
    const checkScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
      ticking = false;
    };
    
    checkScroll(); // Check on mount
    
    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        rafId = requestAnimationFrame(checkScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Browse Vendors', href: '/browse-vendors' },
    { name: 'How It Works', href: '/how-it-works' },
    { name: 'About', href: '/about' },
  ];

  // Header should be transparent on homepage, About, Help Center, Contact, Safety, Vendor Success, Terms, and Privacy when not scrolled
  const shouldBeTransparent = (isHomePage || isAboutPage || isHelpCenterPage || isContactPage || isSafetyPage || isVendorSuccessPage || isTermsPage || isPrivacyPage) && !isScrolled;
  
  // Check if user is admin
  const isAdmin = user?.user_type === 'admin';

  return (
    <header 
      className={`sticky top-0 z-[10000] transition-all duration-300 ${
        shouldBeTransparent
          ? ''
          : 'bg-black shadow-lg border-b border-black'
      }`}
      style={shouldBeTransparent ? { backgroundColor: 'transparent', backdropFilter: 'none' } : {}}
    >
      <div className="page-container">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/images/VendaLOGO1.png" alt="Logo" className="w-36 h-12" />
          </Link>
          

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 text-sm font-light transition-colors ${
                  shouldBeTransparent
                    ? 'text-white hover:text-white drop-shadow-md'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated && <NotificationBell className="mr-2" />}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center space-x-2 transition-colors ${
                    shouldBeTransparent
                      ? 'text-white hover:text-white drop-shadow-md'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    shouldBeTransparent ? 'bg-white/20 ' : 'bg-white'
                  }`}>
                    <User className={`w-4 h-4 ${shouldBeTransparent ? 'text-white' : 'text-black'}`} />
                  </div>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-[10001] border border-gray-200">
                    {/* User Info Section */}
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="text-sm font-medium text-black">
                        {user?.name || 'User'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1 truncate">
                        {user?.email}
                      </div>
                    </div>
                    
                    {/* Menu Items */}
                    {isAdmin ? (
                      <Link
                        to="/admin"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Shield className="w-4 h-4 mr-3" />
                        Go to Admin
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/dashboard"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Calendar className="w-4 h-4 mr-3" />
                          My Bookings
                        </Link>
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4 mr-3" />
                          Profile Settings
                        </Link>
                      </>
                    )}
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    shouldBeTransparent
                      ? 'text-white hover:text-white drop-shadow-md'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className={`btn-primary px-3 text-sm font-light transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95 ${
                    shouldBeTransparent
                      ? 'bg-white text-black hover:bg-gray-50 shadow-lg'
                      : 'bg-white text-black hover:bg-gray-50 shadow-md hover:shadow-lg'
                  }`}
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 rounded-md transition-colors ${
              shouldBeTransparent
                ? 'text-white hover:bg-white/10 drop-shadow-md'
                : 'text-gray-300 hover:text-white hover:bg-gray-800'
            }`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation - Compact Black Side Drawer */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="md:hidden fixed inset-0 top-16 z-[9999] bg-black/50 backdrop-blur-sm"
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Side Drawer */}
            <div 
              className="md:hidden fixed top-16 right-0 h-[calc(100vh-4rem)] w-64 bg-black z-[10000] shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto"
            >
              {/* Navigation Links */}
              <div className="px-4 pt-4 pb-4">
                <nav className="space-y-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block text-white text-sm font-medium py-2.5 px-3 rounded-md hover:bg-gray-800 hover:text-white transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </nav>

                {/* Separator */}
                <div className="border-t border-gray-700 my-4"></div>

                {/* Auth Section */}
                {isAuthenticated ? (
                  <div className="space-y-2">
                    {/* User Info Section */}
                    <div className="px-3 py-3 border-b border-gray-700">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-7 h-7 bg-gray-700 rounded-full flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">
                            {user?.name || 'User'}
                          </div>
                          <div className="text-gray-400 text-xs truncate mt-0.5">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                    </div>
                    {isAdmin ? (
                      <Link
                        to="/admin"
                        className="block text-white text-sm font-medium py-2.5 px-3 rounded-md hover:bg-gray-800 hover:text-white transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Go to Admin
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/dashboard"
                          className="block text-white text-sm font-medium py-2.5 px-3 rounded-md hover:bg-gray-800 hover:text-white transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          My Bookings
                        </Link>
                        <Link
                          to="/profile"
                          className="block text-white text-sm font-medium py-2.5 px-3 rounded-md hover:bg-gray-800 hover:text-white transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Profile Settings
                        </Link>
                      </>
                    )}
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left text-white text-sm font-medium py-2.5 px-3 rounded-md hover:bg-gray-800 hover:text-white transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link
                      to="/login"
                      className="block text-white text-sm font-medium py-2.5 px-3 rounded-md  hover:text-white transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="block w-full bg-white text-gray-900 text-center text-sm font-medium py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default ClientHeader;
